import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Button, Typography, Alert } from '@mui/material';
import { CameraAlt, PhotoCamera, Replay } from '@mui/icons-material';

/**
 * Capture a receipt photo via device camera (getUserMedia) with file-input fallback.
 */
const ReceiptCameraCapture = ({ onCapture, disabled }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(true);
  const [cameraError, setCameraError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [capturedFile, setCapturedFile] = useState(null);
  const [captureError, setCaptureError] = useState('');

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    const video = videoRef.current;
    if (video) {
      video.srcObject = null;
    }
  }, []);

  const syncVideoReadyState = useCallback(() => {
    const video = videoRef.current;
    const hasFrame = !!(video && video.videoWidth > 0 && video.videoHeight > 0);
    setCameraReady(hasFrame);
    return hasFrame;
  }, []);

  const attachStream = useCallback(async (stream) => {
    streamRef.current = stream;
    const video = videoRef.current;
    if (!video) return false;
    video.srcObject = stream;
    try {
      await video.play();
    } catch (playErr) {
      console.warn('Video play:', playErr);
    }
    return syncVideoReadyState();
  }, [syncVideoReadyState]);

  const startCamera = useCallback(async () => {
    setCameraError('');
    setCaptureError('');
    setCameraStarting(true);
    setCameraReady(false);
    stopStream();

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera not available in this browser. Use Choose image below.');
      setCameraStarting(false);
      return;
    }

    const constraintOptions = [
      { video: { facingMode: { ideal: 'environment' } }, audio: false },
      { video: { facingMode: 'user' }, audio: false },
      { video: true, audio: false },
    ];

    let stream = null;
    let lastErr = null;
    for (const constraints of constraintOptions) {
      try {
        // eslint-disable-next-line no-await-in-loop
        stream = await navigator.mediaDevices.getUserMedia(constraints);
        break;
      } catch (err) {
        lastErr = err;
      }
    }

    if (!stream) {
      setCameraError(
        lastErr?.name === 'NotAllowedError'
          ? 'Camera permission denied. Allow camera access in the browser, or use Choose image.'
          : (lastErr?.message || 'Could not open camera. Use Choose image below.')
      );
      setCameraStarting(false);
      return;
    }

    const ready = await attachStream(stream);
    setCameraStarting(false);
    if (!ready) {
      // Stream is live; wait for loadedmetadata (Windows often needs a frame before dimensions exist).
      const video = videoRef.current;
      if (video) {
        const onReady = () => {
          if (syncVideoReadyState()) {
            video.removeEventListener('loadedmetadata', onReady);
            video.removeEventListener('playing', onReady);
          }
        };
        video.addEventListener('loadedmetadata', onReady);
        video.addEventListener('playing', onReady);
      }
    }
  }, [attachStream, stopStream, syncVideoReadyState]);

  useEffect(() => {
    startCamera();
    return () => {
      stopStream();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyCapture = (file, url) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setCapturedFile(file);
    setPreviewUrl(url);
    setCaptureError('');
    stopStream();
    setCameraReady(false);
    onCapture?.(file);
  };

  const takePhoto = () => {
    setCaptureError('');
    const video = videoRef.current;
    if (!video) {
      setCaptureError('Camera not ready. Try Choose image, or wait a moment and try again.');
      return;
    }
    if (!video.videoWidth || !video.videoHeight) {
      setCaptureError('No camera picture yet. Wait for the preview to appear, or use Choose image.');
      syncVideoReadyState();
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setCaptureError('Could not capture photo. Try Choose image instead.');
          return;
        }
        const file = new File([blob], `receipt-${Date.now()}.jpg`, { type: 'image/jpeg' });
        applyCapture(file, URL.createObjectURL(blob));
      },
      'image/jpeg',
      0.92
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setCameraError('Please choose an image file.');
      return;
    }
    applyCapture(file, URL.createObjectURL(file));
    e.target.value = '';
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setCapturedFile(null);
    setCaptureError('');
    onCapture?.(null);
    startCamera();
  };

  const showLiveCamera = !previewUrl;

  return (
    <Box>
      {cameraError && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {cameraError}
        </Alert>
      )}
      {captureError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setCaptureError('')}>
          {captureError}
        </Alert>
      )}

      {previewUrl ? (
        <Box>
          <Box
            component="img"
            src={previewUrl}
            alt="Receipt preview"
            sx={{
              width: '100%',
              maxHeight: 320,
              objectFit: 'contain',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.100',
            }}
          />
          <Button
            startIcon={<Replay />}
            onClick={retake}
            disabled={disabled}
            sx={{ mt: 1 }}
            size="small"
          >
            Retake photo
          </Button>
        </Box>
      ) : (
        <Box>
          {/* Video must stay mounted before stream attach (fixes black preview on Windows). */}
          <Box
            component="video"
            ref={videoRef}
            autoPlay
            playsInline
            muted
            onLoadedMetadata={syncVideoReadyState}
            onPlaying={syncVideoReadyState}
            sx={{
              width: '100%',
              maxHeight: 320,
              minHeight: 180,
              borderRadius: 1,
              bgcolor: 'black',
              objectFit: 'cover',
              display: showLiveCamera ? 'block' : 'none',
            }}
          />
          {cameraStarting && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Starting camera…
            </Typography>
          )}
          {!cameraStarting && !cameraReady && !cameraError && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Camera is on but preview is still loading. If it stays black, use Choose image.
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<CameraAlt />}
              onClick={takePhoto}
              disabled={disabled || cameraStarting}
              sx={{ minHeight: 48, flex: { xs: '1 1 100%', sm: '1 1 auto' } }}
            >
              Take photo
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<PhotoCamera />}
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              sx={{ minHeight: 48, flex: { xs: '1 1 100%', sm: '1 1 auto' } }}
            >
              Choose image
            </Button>
          </Box>
        </Box>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFileChange}
      />

      {capturedFile && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Photo ready ({Math.round(capturedFile.size / 1024)} KB)
        </Typography>
      )}
    </Box>
  );
};

export default ReceiptCameraCapture;
